import { useMemo, useCallback, useState, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Flex, Heading, Tabs, TabPanels, TabPanel, TabList, Tab, Box, useDisclosure } from '@chakra-ui/react';
import { differenceInDays } from 'date-fns';
import { UploadButtonV2 } from 'components/itemData/UploadButtonV2';
import { UploadModalV2 } from 'components/utils/UploadModalV2';
import { SortOrderContext } from 'components/grid/utils/SortOrderContext';

import { getValidRowsWithDates } from 'services/grid';
import {
    GRID_KEYS,
    createRenewableData,
    getRenewableData,
    updateRenewableData,
    deleteRenewableData,
    deleteAllRenewableData,
    createRenewableForecasts,
    getRenewableForecast,
    updateRenewableForecast,
    deleteRenewableForecasts,
    deleteAllRenewableForecasts,
    createRenewableCosts,
    getRenewableCosts,
    updateRenewableCosts,
    deleteRenewableCosts,
    deleteAllRenewableCosts,
    validateRenewableForecasts,
    validateRenewableData,
} from 'services/renewables';

import {
    refreshMultisort,
    exportItemDataGeneric,
    buildAutoformUploadQueryParams,
    capitalizeFirstLetter,
} from '../../../../services/items';
import SubItemData from 'components/itemData/SubItemData';
import DownloadModal from 'components/utils/DownloadModal';
import SecondaryButton from 'components/utils/SecondaryButton';

import { ReactComponent as RefreshIcon } from 'icons/refresh.svg';
import { ReactComponent as DownloadIcon } from 'icons/download.svg';
import { ReactComponent as FunctionIcon } from 'icons/mdi-function-variant.svg';

import { SUBSCHEMACODE_RENEWABLES } from 'constants/uploadDownload';
import CalculateForecastModal from './CalculateForecastModal';
import CalculateCostsCurtailmentsModal from './CalculateCostsCurtailmentsModal';
import ConfirmationModal from '../../../modal/ConfirmationModal';
import useCommonToast from '../../../../hooks/useCommonToast';
import useItemCreationWizard from 'hooks/useItemCreationWizard';

const SubRenewableData = ({
    itemLabel,
    subRenewableId,
    itemId,
    itemTypeId,
    definition,
    helpPageUrlItemTypeId = null,
    onIssuesChange = null,
}) => {
    const forecastTypes = useSelector((state) => state.forecast.types);
    const intl = useIntl();
    const { toast } = useCommonToast();

    const { projectOnOffDates } = useItemCreationWizard();

    const downloadModal = useDisclosure();
    const uploadModal = useDisclosure();
    const calculateForecastModal = useDisclosure();
    const calculateCostsCurtailmentsModal = useDisclosure();
    const deleteExistingForecastsConfirmationModal = useDisclosure();
    const deleteExistingCostsConfirmationModal = useDisclosure();

    const { setSortOrder } = useContext(SortOrderContext);

    const bookId = useSelector((state) => state.connections.book);
    const uploadParameters = [`${bookId}`, `${itemTypeId}`];
    const projectDates = projectOnOffDates.find((x) => x.subItemId === subRenewableId);

    const [key, setKey] = useState(0);
    const [selectedTab, setSelectedTab] = useState(0);
    const [loadingQueue, setLoadingQueue] = useState([]); // unique keys represeting which resource is in loading state

    const [historicalDataHasErrors, setHistoricalDataHasErrors] = useState(false);
    const [forecastHasErrors, setForecastHasErrors] = useState(false);
    const [costsHasErrors, setCostsHasErrors] = useState(false);
    const [isCheckingHistoricalData, setIsCheckingHistoricalData] = useState(false);
    const [hasHistoricalData, setHasHistoricalData] = useState(false);

    const refreshTabs = useCallback(() => {
        setKey((prev) => prev + 1);
        setSortOrder((prev) => ({ ...prev, ...refreshMultisort(Object.values(GRID_KEYS)) }));
    }, [setSortOrder]);

    const onLoadingStart = useCallback((key) => {
        setLoadingQueue((prev) => prev.concat(key));
    }, []);

    const onLoadingEnd = useCallback((key) => {
        setLoadingQueue((prev) => prev.filter((p) => p !== key));
    }, []);

    //using custom filters to display all the data as the UI does not support date range filtering
    const filters = useMemo(() => {
        return {
            startDateFrom: '1900-01-01T00:00:00.000Z',
            startDateTo: '2100-01-01T00:00:00.000Z',
            filterType: 1,
            updateDateFrom: '1900-01-01T00:00:00.000Z',
            updateDateTo: '2100-01-01T00:00:00.000Z',
        };
    }, []);

    const dataUnitLabel = intl.formatMessage(
        { id: 'common_item_unit_label' },
        { item: capitalizeFirstLetter(itemLabel), unit: 'MW' }
    );

    const dataColumns = useMemo(
        () => [
            {
                type: 'date',
                field: 'startDate',
                headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
                initialSort: 'asc',
                initialSortIndex: 1,
            },
            {
                type: 'date',
                field: 'endDate',
                headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
            },
            {
                field: 'windOrSolar',
                type: 'number',
                headerName: dataUnitLabel,
            },
            {
                type: 'date',
                field: 'updateDateTime',
                headerName: intl.formatMessage({ id: 'common_grid_created' }),
                initialSort: 'asc',
                initialSortIndex: 2,
            },
        ],
        [intl, dataUnitLabel]
    );

    const dataGridResource = useMemo(() => {
        return {
            create: (rows) => createRenewableData(subRenewableId, rows),
            read: (pagination) => getRenewableData(subRenewableId, { ...pagination, ...filters }),
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, ['startDate', 'endDate', 'updateDateTime']);

                return updateRenewableData(subRenewableId, validRows);
            },
            delete: (rows) =>
                deleteRenewableData(
                    subRenewableId,
                    rows.map((row) => row.windDataId)
                ),
            deleteAll: () => deleteAllRenewableData(subRenewableId),
            validate: (rows) => validateRenewableData(subRenewableId, rows),
        };
    }, [subRenewableId, filters]);

    const forecastColumns = useMemo(
        () => [
            {
                field: 'forecastTypeId',
                type: 'select',
                cellEditorParams: { options: forecastTypes, required: true },
                headerName: intl.formatMessage({ id: 'common_grid_forecast_type' }),
            },
            {
                type: 'date',
                field: 'startDate',
                headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
                initialSort: 'asc',
                initialSortIndex: 1,
            },
            {
                type: 'date',
                field: 'endDate',
                headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
            },
            {
                field: 'expectedEnergy',
                type: 'number',
                headerName: intl.formatMessage(
                    { id: 'common_item_unit_label' },
                    { item: 'Expected Energy', unit: 'MWh' }
                ),
            },
            {
                field: 'expectedPeakGeneration',
                type: 'number',
                headerName: intl.formatMessage(
                    { id: 'common_item_unit_label' },
                    { item: 'Expected Peak Generation', unit: 'MW' }
                ),
            },
            {
                field: 'capacity',
                type: 'number',
                headerName: intl.formatMessage({ id: 'common_item_unit_label' }, { item: 'Capacity', unit: 'MW' }),
            },
            {
                type: 'date',
                field: 'updateDateTime',
                headerName: intl.formatMessage({ id: 'common_grid_created' }),
                initialSort: 'asc',
                initialSortIndex: 2,
            },
        ],
        [intl, forecastTypes]
    );

    const forecastGridResource = useMemo(() => {
        return {
            create: (rows) => createRenewableForecasts(subRenewableId, rows),
            read: (pagination) => getRenewableForecast(subRenewableId, { ...pagination, ...filters }),
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, ['startDate', 'endDate', 'updateDateTime']);

                return updateRenewableForecast(subRenewableId, validRows);
            },
            delete: (rows) =>
                deleteRenewableForecasts(
                    subRenewableId,
                    rows.map((row) => row.windForecastId)
                ),
            deleteAll: () => deleteAllRenewableForecasts(subRenewableId),
            validate: (rows) => validateRenewableForecasts(subRenewableId, rows),
        };
    }, [subRenewableId, filters]);

    const costsColumns = useMemo(
        () => [
            {
                type: 'date',
                field: 'startDate',
                headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
                initialSort: 'asc',
                initialSortIndex: 1,
            },
            {
                type: 'date',
                field: 'endDate',
                headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
            },
            {
                field: 'fixedCost',
                type: 'number',
                headerName: intl.formatMessage(
                    { id: 'common_item_unit_label' },
                    { item: 'Fixed Cost', unit: '$/month' }
                ),
            },
            {
                field: 'vom',
                type: 'number',
                headerName: intl.formatMessage({ id: 'common_item_unit_label' }, { item: 'VOM', unit: '$/MWh' }),
            },
            {
                field: 'curtailPrice',
                type: 'number',
                headerName: intl.formatMessage({ id: 'common_grid_curtailment_price' }),
            },
            {
                field: 'curtailPercentage',
                type: 'number',
                headerName: intl.formatMessage(
                    { id: 'common_item_unit_label' },
                    { item: 'Curtailment Percentage', unit: '%, 0-100' }
                ),
            },
            {
                type: 'date',
                field: 'updateDateTime',
                headerName: intl.formatMessage({ id: 'common_grid_created' }),
                initialSort: 'asc',
                initialSortIndex: 2,
            },
        ],
        [intl]
    );

    const costsGridResource = useMemo(() => {
        return {
            create: (rows) => createRenewableCosts(subRenewableId, rows),
            read: (pagination) => getRenewableCosts(subRenewableId, { ...pagination }),
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, ['startDate', 'endDate', 'updateDateTime']);

                return updateRenewableCosts(subRenewableId, validRows);
            },
            delete: (rows) =>
                deleteRenewableCosts(
                    subRenewableId,
                    rows.map((row) => row.windIdCostId)
                ),
            deleteAll: () => deleteAllRenewableCosts(subRenewableId),
        };
    }, [subRenewableId]);

    const onDownloadGeneric = useCallback(
        (templateOnly = false) => {
            const itemCompId = subRenewableId;

            const now = new Date();
            const min = filters?.startDateFrom;
            const filterDate = min ? new Date(min) : null;
            const daysBack = filterDate ? Math.abs(differenceInDays(filterDate, now)) : null;
            return exportItemDataGeneric(SUBSCHEMACODE_RENEWABLES, itemId, daysBack, itemCompId, -1, templateOnly);
        },
        [itemId, subRenewableId, filters]
    );

    const onDataIssuesChange = useCallback((hasErrors) => {
        setHistoricalDataHasErrors(hasErrors);
    }, []);

    const onForecastIssuesChange = useCallback((hasErrors) => {
        setForecastHasErrors(hasErrors);
    }, []);

    const onCostsIssuesChange = useCallback((hasErrors) => {
        setCostsHasErrors(hasErrors);
    }, []);

    useEffect(() => {
        if (historicalDataHasErrors || forecastHasErrors || costsHasErrors) {
            onIssuesChange(subRenewableId, true);
        } else {
            onIssuesChange(subRenewableId, false);
        }
    }, [historicalDataHasErrors, forecastHasErrors, costsHasErrors, onIssuesChange, subRenewableId]);

    const openForecastModal = useCallback(async () => {
        setIsCheckingHistoricalData(true);

        //check if the project dates are valid
        const onlineDateValue = definition?.onlineDate || projectDates.enteredOnlineDate;
        const offlineDateValue = definition?.offlineDate || projectDates.enteredOfflineDate;
        if (!onlineDateValue || !offlineDateValue) {
            toast({
                message: intl.formatMessage({ id: 'item_creation_wizard_project_dates_required_warning' }),
                status: 'error',
            });
            setIsCheckingHistoricalData(false);
            return;
        }

        //determine if there is existing forecast data
        const pagination = {
            paging: {
                pageSize: 10,
                page: 1,
            },
        };
        const forecastData = await forecastGridResource.read(pagination);

        if (forecastData?.items?.length > 0) {
            // existing forecasts, show confirmation modal
            deleteExistingForecastsConfirmationModal.onOpen();
        } else {
            // no existing forecast data, open the modal
            calculateForecastModal.onOpen();
        }

        setIsCheckingHistoricalData(false);
    }, [
        forecastGridResource,
        calculateForecastModal,
        deleteExistingForecastsConfirmationModal,
        toast,
        intl,
        projectDates,
        setIsCheckingHistoricalData,
        definition,
    ]);

    // Update the tab change handler to check for historical data when the forecast tab is selected
    const handleTabChange = useCallback(
        (index) => {
            setSelectedTab(index);

            // Check for historical data only when navigating to the forecast tab (index 1)
            // or costs tab (index 2)
            if (index === 1 || index === 2) {
                const checkForHistoricalData = async () => {
                    try {
                        setIsCheckingHistoricalData(true);
                        const pagination = {
                            paging: {
                                pageSize: 1,
                                page: 1,
                            },
                        };
                        const data = await dataGridResource.read(pagination);
                        setHasHistoricalData(data?.items?.length > 0);
                    } catch (error) {
                        console.error('Error checking for historical data:', error);
                        setHasHistoricalData(false);
                    } finally {
                        setIsCheckingHistoricalData(false);
                    }
                };

                checkForHistoricalData();
            }
        },
        [dataGridResource, setIsCheckingHistoricalData]
    );

    const customForecastGridActions = useMemo(() => {
        if (!hasHistoricalData) {
            return [];
        }

        const tooltipText = isCheckingHistoricalData
            ? intl.formatMessage({ id: 'item_creation_wizard_checking_data' })
            : intl.formatMessage({ id: 'item_creation_wizard_calculate_from_data' });
        return [
            {
                leftIcon: <FunctionIcon />,
                text: 'Calculate Forecasts',
                onClick: openForecastModal,
                tooltip: tooltipText,
                isLoading: isCheckingHistoricalData,
            },
        ];
    }, [isCheckingHistoricalData, openForecastModal, hasHistoricalData, intl]);

    const openCostsModal = useCallback(async () => {
        setIsCheckingHistoricalData(true);

        //check if the project dates are valid
        const onlineDateValue = definition?.onlineDate || projectDates.enteredOnlineDate;
        const offlineDateValue = definition?.offlineDate || projectDates.enteredOfflineDate;
        if (!onlineDateValue || !offlineDateValue) {
            toast({
                message: intl.formatMessage({ id: 'item_creation_wizard_project_dates_required_warning' }),
                status: 'error',
            });
            setIsCheckingHistoricalData(false);
            return;
        }

        //determine if there is existing costs data
        const pagination = {
            paging: {
                pageSize: 10,
                page: 1,
            },
        };
        const costsData = await costsGridResource.read(pagination);

        if (costsData?.items?.length > 0) {
            // existing costs, show confirmation modal
            deleteExistingCostsConfirmationModal.onOpen();
        } else {
            // no existing costs data, open the modal
            calculateCostsCurtailmentsModal.onOpen();
        }

        setIsCheckingHistoricalData(false);
    }, [
        costsGridResource,
        calculateCostsCurtailmentsModal,
        deleteExistingCostsConfirmationModal,
        toast,
        intl,
        projectDates,
        setIsCheckingHistoricalData,
        definition,
    ]);

    const customCostGridActions = useMemo(() => {
        if (!hasHistoricalData) {
            return [];
        }

        const tooltipText = isCheckingHistoricalData
            ? intl.formatMessage({ id: 'item_creation_wizard_checking_data' })
            : intl.formatMessage({ id: 'item_creation_wizard_calculate_from_data' });
        return [
            {
                leftIcon: <FunctionIcon />,
                text: 'Calculate Costs',
                onClick: openCostsModal,
                tooltip: tooltipText,
                isLoading: isCheckingHistoricalData,
            },
        ];
    }, [isCheckingHistoricalData, openCostsModal, hasHistoricalData, intl]);

    return (
        <>
            <Tabs
                px={0}
                mt={6}
                isLazy
                isManual
                lazyBehavior="keepMounted"
                key={key}
                index={selectedTab}
                onChange={handleTabChange}
            >
                <TabList w="100%" flexWrap={{ base: 'wrap' }} alignItems="center">
                    <Tab>
                        <FormattedMessage id="common_historical_data_data" />
                    </Tab>

                    <Tab>
                        <FormattedMessage id="common_historical_data_forecast" />
                    </Tab>

                    <Tab>
                        <FormattedMessage id="common_historical_data_costs" />
                    </Tab>

                    <Flex
                        ml={{ base: 'auto' }}
                        mb={{ base: 1 }}
                        order={{ base: -1, xl: 0 }}
                        w={{ base: '100%', xl: 'auto' }}
                        flexWrap={{ base: 'wrap' }}
                    >
                        <SecondaryButton
                            leftIcon={<DownloadIcon />}
                            w="auto !important"
                            ml={{ base: 0 }}
                            mr={{ base: 2 }}
                            mb={{ base: 1 }}
                            mt={{ base: 1 }}
                            size="sm"
                            type="button"
                            variant="secondary"
                            onClick={downloadModal.onOpen}
                        >
                            <FormattedMessage id="common_download_btn_tooltip_and_label" />
                        </SecondaryButton>

                        <UploadButtonV2
                            hasLabel={true}
                            ml={{ base: 0 }}
                            mr={{ base: 2 }}
                            mb={{ base: 1 }}
                            mt={{ base: 1 }}
                            w="auto !important"
                            onClick={uploadModal.onOpen}
                        />

                        <SecondaryButton
                            onClick={refreshTabs}
                            size="sm"
                            variant="secondary"
                            leftIcon={<RefreshIcon />}
                            isLoading={loadingQueue.length > 0}
                            loadingText={intl.formatMessage({ id: 'common_loading' })}
                            textTransform="capitalize"
                            mr={{ base: 2 }}
                            mb={{ base: 1 }}
                            mt={{ base: 1 }}
                        >
                            <FormattedMessage id="common_refresh" />
                        </SecondaryButton>
                    </Flex>
                </TabList>

                <TabPanels mt={4}>
                    <TabPanel>
                        <Heading as="h3" variant="h3" textTransform="capitalize">
                            <FormattedMessage id="common_historical_data_heading" values={{ item: itemLabel }} />
                        </Heading>

                        <SubItemData
                            hasChart={false}
                            tab="0"
                            name={GRID_KEYS.renewableData}
                            yAxisLabel={dataUnitLabel}
                            onLoadingStart={onLoadingStart}
                            onLoadingEnd={onLoadingEnd}
                            gridResource={dataGridResource}
                            columns={dataColumns}
                            addDataColumns={dataColumns}
                            onIssuesChange={onDataIssuesChange}
                        />
                    </TabPanel>

                    <TabPanel>
                        <Heading as="h3" variant="h3" textTransform="capitalize">
                            <FormattedMessage
                                id="common_historical_data_forecast_heading"
                                values={{ item: itemLabel }}
                            />
                        </Heading>

                        <SubItemData
                            hasChart={false}
                            tab="1"
                            name={GRID_KEYS.renewableForecast}
                            yAxisLabel={dataUnitLabel}
                            onLoadingStart={onLoadingStart}
                            onLoadingEnd={onLoadingEnd}
                            gridResource={forecastGridResource}
                            columns={forecastColumns}
                            addDataColumns={forecastColumns}
                            primaryKeyName="windForecastId"
                            onIssuesChange={onForecastIssuesChange}
                            customGridActions={customForecastGridActions}
                        />
                    </TabPanel>

                    <TabPanel>
                        <Heading as="h3" variant="h3" textTransform="capitalize">
                            <FormattedMessage id="common_historical_data_costs_heading" values={{ item: itemLabel }} />
                        </Heading>

                        <SubItemData
                            tab="2"
                            name={GRID_KEYS.renewableCosts}
                            onLoadingStart={onLoadingStart}
                            onLoadingEnd={onLoadingEnd}
                            gridResource={costsGridResource}
                            columns={costsColumns}
                            addDataColumns={costsColumns}
                            onIssuesChange={onCostsIssuesChange}
                            customGridActions={customCostGridActions}
                        />
                    </TabPanel>
                </TabPanels>
            </Tabs>
            {downloadModal.isOpen && (
                <DownloadModal
                    isOpen
                    onClose={downloadModal.onClose}
                    onDownloadData={onDownloadGeneric}
                    onDownloadTemplate={() => onDownloadGeneric(true)}
                />
            )}
            {uploadModal.isOpen && (
                <UploadModalV2
                    isOpen
                    onClose={uploadModal.onClose}
                    route="auto-forms/import-data"
                    includeUpdateTypeSelect={true}
                    requireConfirmation={true}
                    onAfterUploadChange={refreshTabs}
                    queryParams={buildAutoformUploadQueryParams(itemId, SUBSCHEMACODE_RENEWABLES, uploadParameters)}
                />
            )}
            {calculateForecastModal.isOpen && (
                <CalculateForecastModal
                    isOpen
                    onClose={calculateForecastModal.onClose}
                    subRenewableId={subRenewableId}
                    onRefresh={refreshTabs}
                    nameplateCapacity={null}
                    definition={definition}
                />
            )}
            {calculateCostsCurtailmentsModal.isOpen && (
                <CalculateCostsCurtailmentsModal
                    isOpen
                    onClose={calculateCostsCurtailmentsModal.onClose}
                    subRenewableId={subRenewableId}
                    onRefresh={refreshTabs}
                    definition={definition}
                />
            )}
            {deleteExistingForecastsConfirmationModal.isOpen && (
                <ConfirmationModal
                    isOpen
                    onClose={deleteExistingForecastsConfirmationModal.onClose}
                    header={<FormattedMessage id="common_confirm" />}
                    hasExtraStep
                    content={<FormattedMessage id="item_creation_wizard_forecast_confirmation_modal" />}
                    confirmText={
                        <Box as="span" textTransform="capitalize">
                            <FormattedMessage id="common_continue" />
                        </Box>
                    }
                    onConfirm={() => {
                        deleteExistingForecastsConfirmationModal.onClose();
                        calculateForecastModal.onOpen();
                    }}
                />
            )}
            {deleteExistingCostsConfirmationModal.isOpen && (
                <ConfirmationModal
                    isOpen
                    onClose={deleteExistingCostsConfirmationModal.onClose}
                    header={<FormattedMessage id="common_confirm" />}
                    hasExtraStep
                    content={<FormattedMessage id="item_creation_wizard_costs_curtailments_confirmation_modal" />}
                    confirmText={
                        <Box as="span" textTransform="capitalize">
                            <FormattedMessage id="common_continue" />
                        </Box>
                    }
                    onConfirm={() => {
                        deleteExistingCostsConfirmationModal.onClose();
                        calculateCostsCurtailmentsModal.onOpen();
                    }}
                />
            )}
        </>
    );
};

export default SubRenewableData;
